import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { Navigate } from 'react-router';
import { readConfig } from '../api/config';
import { useAuth } from '../auth/AuthContext';
import { loadGoogleIdentity } from '../auth/google';
import styles from './Login.module.css';

/**
 * The only unauthenticated screen: "Entrar com Google" and nothing else
 * (02-requirements.md §Admin Authentication, Google-only since 2026-09-27).
 * Google proves who is signing in; `admin_accounts` decides whether — it is
 * seeded from ADMIN_ALLOWED_EMAILS and nothing in the panel can add a row.
 *
 * The copy below is the panel's; the keys it maps are the API's own
 * machine-readable ones, left untranslated in the payload.
 */
const MESSAGES: Record<string, string> = {
  not_allowlisted: 'Esta conta Google não tem permissão para administrar as enquetes.',
  invalid_google_token: 'O Google não confirmou esta conta. Tente de novo.',
  google_auth_not_configured: 'O login com Google não está configurado no servidor.',
  google_not_configured: 'O login com Google não está configurado neste painel.',
  google_unavailable: 'Não foi possível carregar o login do Google. Verifique a conexão.',
  too_many_attempts: 'Muitas tentativas seguidas. Espere alguns minutos e tente de novo.',
  session_expired: 'Sua sessão expirou. Entre novamente.',
  network_error: 'Não foi possível falar com a API.',
};

/** The button's width in px — Google renders it at a fixed size, not in CSS. */
const BUTTON_WIDTH = 356;

export function Login(): ReactNode {
  const { admin, signIn, failSignIn, signingIn, error } = useAuth();
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (admin) return;
    const { googleClientId } = readConfig();
    if (!googleClientId) {
      failSignIn('google_not_configured');
      return;
    }

    let cancelled = false;
    loadGoogleIdentity()
      .then((google) => {
        if (cancelled || !buttonRef.current) return;
        google.initialize({
          client_id: googleClientId,
          callback: ({ credential }) => void signIn(credential),
          ux_mode: 'popup',
          auto_select: false,
          cancel_on_tap_outside: true,
        });
        google.renderButton(buttonRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          width: BUTTON_WIDTH,
          locale: 'pt-BR',
        });
      })
      .catch(() => {
        if (!cancelled) failSignIn('google_unavailable');
      });
    return () => {
      cancelled = true;
    };
  }, [admin, signIn, failSignIn]);

  if (admin) return <Navigate to="/" replace />;

  return (
    <div className={styles.screen}>
      <div className={styles.card}>
        <h1 className={styles.title}>Administração</h1>
        <p className={styles.hint}>Entre com a conta Google autorizada a administrar as enquetes.</p>

        <div className={styles.google} ref={buttonRef} data-testid="google-signin" />

        {signingIn ? <p className={styles.hint}>Entrando…</p> : null}

        {error ? (
          <p className={styles.error} role="alert">
            {MESSAGES[error] ?? 'Não foi possível entrar.'}
          </p>
        ) : null}
      </div>
    </div>
  );
}
